import type { VarsomProblem } from '../data/varsom.ts';
import {
  loadRealConditions,
  realDataDate,
  realDataHistoryDays,
  realDataStatus,
  varsomForecast,
} from '../state/realData.ts';
import { terrain, uiMode } from '../state/store.ts';
import { ASPECT_SECTORS } from '../sim/weaklayers.ts';

const HISTORY_OPTIONS = [3, 5, 7, 10];

/** EAWS danger-level colours. */
const DANGER_COLORS = ['#c8ccd0', '#ccff66', '#ffff00', '#ff9900', '#ff0000', '#1a1a1a'];

/**
 * "Real conditions": load seNorge snow and weather plus the Varsom forecast for the loaded
 * area and a date (state/realData.ts), replacing the scenario. Both modes.
 */
export function RealConditionsPanel() {
  const status = realDataStatus.value;
  const loading = status.kind === 'loading';
  const advanced = uiMode.value === 'advanced';
  return (
    <section class="panel-section">
      <h2>Real conditions</h2>
      <p class="hint real-data-intro">
        Snow and weather from seNorge for the loaded area: the days before the chosen time, plus two
        days after (the forecast, when it's today).
      </p>
      <div class="real-data-row">
        <label>
          Date
          <input
            type="date"
            value={realDataDate.value}
            min="2012-09-01"
            onChange={(e) => (realDataDate.value = (e.target as HTMLInputElement).value)}
          />
        </label>
        {advanced ? (
          <label>
            History
            <select
              value={realDataHistoryDays.value}
              onChange={(e) =>
                (realDataHistoryDays.value = Number((e.target as HTMLSelectElement).value))
              }
            >
              {HISTORY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <button
        type="button"
        class="primary"
        disabled={!terrain.value || loading}
        onClick={() => void loadRealConditions()}
      >
        {loading ? 'Loading…' : 'Load real conditions'}
      </button>
      {!terrain.value ? <p class="hint">Load terrain first.</p> : null}
      {status.kind === 'loading' ? <p class="hint">{status.step}</p> : null}
      {status.kind === 'error' ? <p class="error">{status.message}</p> : null}
      {status.kind === 'done' ? (
        <>
          <p class="hint">Loaded {status.label}.</p>
          <dl class="info-list real-data-summary">
            <dt>Existing snow (low → high)</dt>
            <dd>
              {status.summary.snowLowCm} → {status.summary.snowHighCm} cm
            </dd>
            <dt>Precipitation (high terrain)</dt>
            <dd>{status.summary.precipitationMm} mm</dd>
            <dt>Weather events</dt>
            <dd>{status.summary.events}</dd>
            <dt>Weak layers from Varsom</dt>
            <dd>{status.summary.weakLayers}</dd>
          </dl>
          {status.summary.warnings.map((w) => (
            <p key={w} class="warning">
              {w}
            </p>
          ))}
        </>
      ) : null}
      <VarsomCard />
      <p class="hint real-data-credits">
        Data: seNorge (NVE, MET Norway), cloud cover via Open-Meteo, forecast from Varsom — CC BY.
      </p>
    </section>
  );
}

function aspectsLabel(aspects: readonly boolean[]): string {
  if (aspects.every(Boolean)) return 'all aspects';
  return ASPECT_SECTORS.filter((_s, k) => aspects[k]).join(', ');
}

function bandsLabel(p: VarsomProblem): string {
  return p.bands
    .map((b) =>
      b.min <= 0 && b.max >= 10_000
        ? 'all elevations'
        : b.min <= 0
          ? `below ${b.max} m`
          : b.max >= 10_000
            ? `above ${b.min} m`
            : `${b.min}–${b.max} m`,
    )
    .join(' and ');
}

/** The Varsom forecast for the area and date, to compare with what the simulation shows. */
function VarsomCard() {
  const v = varsomForecast.value;
  if (!v) return null;
  const color = DANGER_COLORS[v.dangerLevel] ?? DANGER_COLORS[0]!;
  return (
    <div class="varsom-card">
      <div class="varsom-header">
        <span
          class="varsom-danger"
          style={{ background: color, color: v.dangerLevel >= 4 ? '#fff' : '#1a1a1a' }}
        >
          {v.dangerLevel || '–'}
        </span>
        <div>
          <strong>{v.dangerLevel ? v.dangerLevelName : 'No rating'}</strong>
          <div class="hint">
            Varsom · {v.regionName} · {v.date}
          </div>
        </div>
      </div>
      {v.problems.length > 0 ? (
        <ul class="varsom-problems">
          {v.problems.map((p, k) => (
            <li key={k}>
              <strong>{p.typeName}</strong>
              {p.causeName ? <div>{p.causeName}</div> : null}
              <div class="hint">
                {aspectsLabel(p.aspects)}, {bandsLabel(p)}
                {p.sensitivity ? ` · ${p.sensitivity.toLowerCase()}` : ''}
                {p.size ? ` · size ${p.size.toLowerCase()}` : ''}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      <a href={v.url} target="_blank" rel="noopener noreferrer" class="varsom-link">
        Full forecast on varsom.no ↗
      </a>
    </div>
  );
}
